import { useEffect, useRef, useState } from "react";
import {
    AnimatePresence,
    motion,
    useMotionValue,
    useSpring,
    useTransform,
} from "framer-motion";
import { Flame } from "lucide-react";
import ArcadeNav from "./ArcadeNav";
import PattyFlipGame from "./PattyFlipGame";

const lineAnim = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: 0,
        transition: { delay: 0.3 + i * 0.16, duration: 0.75, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function GameHero({ onNavigate, visible }) {
    const [intro, setIntro] = useState(false);
    const introDone = useRef(false);

    useEffect(() => {
        if (!visible || introDone.current) return undefined;
        introDone.current = true;
        setIntro(true);
        const t = setTimeout(() => setIntro(false), 3400);
        return () => clearTimeout(t);
    }, [visible]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.2, -2.2]), {
        stiffness: 60,
        damping: 18,
    });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.8, 2.8]), {
        stiffness: 60,
        damping: 18,
    });

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            data-testid="game-hero-section"
            onMouseMove={onMouseMove}
            className="relative flex min-h-screen flex-col overflow-hidden bg-[#060608]"
        >
            <div className="noise-layer" />

            <div className="flex w-full items-end justify-between px-5 pb-2 pt-5 sm:px-10 sm:pt-7">
                <div className="flex items-center gap-3">
                    <img
                        src="/assets/asset2.png"
                        alt="Grill Enforcers badge"
                        className="h-9 w-auto rounded-sm border border-[#2A2A36] object-cover sm:h-11"
                    />
                    <span className="font-jbmono hidden text-[11px] uppercase tracking-[0.3em] text-[#A1A1B2] sm:block">
                        EST. ON THE ICE
                    </span>
                </div>
                <span className="font-pixel text-[8px] text-[#A1A1B2] sm:text-[10px]">
                    PATTY FLIP <span className="text-[#FF3B30]">v1.0</span>
                </span>
            </div>

            <div
                className="flex flex-1 items-center justify-center px-3 py-4 sm:px-10"
                style={{ perspective: 1400 }}
            >
                <motion.div
                    style={{ rotateX, rotateY }}
                    initial={{ opacity: 0, y: 40, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: intro ? 3.5 : 0, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-[1380px]"
                >
                    <PattyFlipGame />
                </motion.div>
            </div>

            <div className="px-4 pb-5 sm:pb-6">
                <ArcadeNav active="game" onNavigate={onNavigate} prefix="hero" />
            </div>

            <AnimatePresence>
                {intro && (
                    <motion.div
                        data-testid="intro-overlay"
                        exit={{ clipPath: "inset(0 0 100% 0)" }}
                        transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
                        onClick={() => setIntro(false)}
                        className="absolute inset-0 z-50 flex cursor-pointer flex-col items-center justify-center bg-[#060608]"
                        style={{ clipPath: "inset(0 0 0% 0)" }}
                    >
                        <div className="noise-layer" />
                        <div className="crt-overlay pointer-events-none absolute inset-0 opacity-60" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.15, duration: 0.5 }}
                            className="mb-6 flex items-center gap-3 sm:mb-10"
                        >
                            <Flame className="h-4 w-4 text-[#FF3B30]" strokeWidth={2.5} />
                            <span className="font-pixel text-[8px] tracking-[0.35em] text-[#A1A1B2] sm:text-[10px]">
                                GRILL ENFORCERS PRESENTS
                            </span>
                            <Flame className="h-4 w-4 text-[#FF3B30]" strokeWidth={2.5} />
                        </motion.div>
                        <div className="overflow-hidden">
                            <motion.h1
                                custom={0}
                                variants={lineAnim}
                                initial="hidden"
                                animate="show"
                                className="font-cond text-center text-[19vw] font-extrabold uppercase leading-[0.82] tracking-tight text-[#F2F2F7] sm:text-[15vw]"
                            >
                                FLIP
                            </motion.h1>
                        </div>
                        <div className="overflow-hidden">
                            <motion.h1
                                custom={1}
                                variants={lineAnim}
                                initial="hidden"
                                animate="show"
                                className="font-cond text-outline-ember text-center text-[19vw] font-extrabold uppercase leading-[0.82] tracking-tight sm:text-[15vw]"
                            >
                                THE PATTY
                            </motion.h1>
                        </div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.5, duration: 0.4 }}
                            className="blink mt-8 font-pixel text-[9px] text-[#FF3B30] sm:mt-12 sm:text-xs"
                        >
                            HOLD SPACE — RELEASE TO FLIP
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
